import { useState } from "react";
import { createdViaLabel } from "@/lib/buyerCreatedVia";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { BadgeCheck, ClipboardCheck, ListChecks, Megaphone, Pencil, Radar, Send, User } from "lucide-react";
import {
  buyersApi,
  buyerSubmissionsApi,
  type BuyerLeadDetailDto,
  type BuyerLeadQualificationDto,
  type BuyerLeadStatus,
  type BuyerSubmissionSummaryDto,
} from "@/api/buyers";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  EntityTabBar,
  CROSS_CUTTING_TAB_ICONS,
  type EntityTabItem,
} from "@/components/layout/EntityTabBar";
import {
  Button,
  EmptyState,
  MissingValue,
  OverflowMenu,
  RECORD_CARD_MIN_WIDTH,
  RECORD_RAIL_WIDTH,
  ResponsiveGrid,
  ResponsiveSplit,
  Skeleton,
  StatusBadge,
  Surface,
  TableValue,
  Timestamp,
  Tooltip,
  type SubTableColumn,
} from "@/design-system/components/ui";
import { formatCurrency } from "@/design-system/lib/format";
import {
  listingNameOrFallback,
  listingShortNameOrFallback,
  resolvedShortListingName,
} from "@/lib/listingDisplay";
import { useBreadcrumbs } from "@/components/layout/BreadcrumbsContext";
import { ActivityTabPanel } from "@/pages/activity/components/ActivityTabPanel";
import { OverviewActivityPreview } from "@/components/activity/OverviewActivityPreview";
import { AppointmentListMini } from "@/pages/calendar/components/AppointmentListMini";
import { CommunicationsTabPanel } from "@/pages/communications/components/CommunicationsTabPanel";
import { DocumentPanel } from "@/pages/documents/components/DocumentPanel";
import { NotesTabPanel } from "@/pages/notes/components/NotesTabPanel";
import { TaskListMini } from "@/pages/tasks/components/TaskListMini";
import { RelatedConversationsPanel } from "@/components/messaging/RelatedConversationsPanel";
import { useRecordDrawerParam } from "@/components/layout/EntityRecordDrawer";
import { SpecialistQuickPick } from "@/components/buyers/SpecialistQuickPick";
import { BuyerNextStepStrip } from "@/components/buyers/BuyerNextStepStrip";
import { buyerKeys, buyerSubmissionKeys } from "@/hooks/buyers/buyerKeys";
import { useAuth } from "@/hooks/useAuth";
import { channelLabel } from "./advertising/advertisingBits";
import { AssociatedListingsTab } from "./associations/AssociatedListingsTab";
import { ResearchTab } from "./research/ResearchTab";
import { BuyerReferredBy, ReferredByBadge } from "./referral/BuyerReferredBy";
import { RESEARCH_LIST_PARAMS } from "./research/researchListFilters";
import { listingAgentColumns } from "./listingAgentColumn";
import { RecordSubmissionsTable, useRecordSubmissions } from "./submission/recordSubmissions";
import {
  ActionsColumnHeader,
  ShowArchivedToggle,
  SubmissionRowMenu,
  submissionArchiveCopy,
  useSubmissionRemoval,
  withArchivedRows,
  type SubmissionRemovalTarget,
} from "./submission/submissionArchive";
import { CombinedIntroSelectionBar } from "./combinedIntro/CombinedIntroSelectionBar";
import { IntroSentCaption, combinedIntroSelectColumn } from "./combinedIntro/combinedIntroColumns";
import {
  useCombinedIntroSelection,
  type CombinedIntroPick,
} from "./combinedIntro/combinedIntroSelection";
import {
  CombinedIntroComposer,
  type CombinedIntroComposerStart,
} from "./combinedIntro/CombinedIntroComposer";
import { combinedIntroCopy } from "./combinedIntro/combinedIntroCopy";
import { useOpenCombinedIntroDraft } from "@/hooks/buyers/useCombinedIntro";
import { buyerLabel } from "./buyerFilters";
import { submissionStatusTone } from "./buyerListBits";
import { describeBuyerPhase } from "./buyerPhases";
import { DetailField, DetailFieldList, DetailSection, MilestoneList, PhaseLadder, isWideFieldType } from "./detailBits";
import { QueryErrorState, isNotFoundError } from "./queryStates";
import { BuyerBiographyPanel } from "./BuyerBiographyPanel";
import { BuyerStatusControl } from "./BuyerStatusControl";
import {
  BuyerSummaryStrip,
  resolveNextFollowUp,
  type BuyerFollowUpChip,
  type BuyerNdaChip,
} from "./BuyerSummaryStrip";
import { ArchiveBuyerModal } from "./archive/ArchiveBuyerModal";
import { BuyerEducationCard } from "./education/BuyerEducationCard";
import { ConvertToBorrowerModal } from "./ConvertToBorrowerModal";
import { LeadSourceField } from "./LeadSourceField";
import { PrequalificationLinkPanel } from "./PrequalificationLinkPanel";
import { PrequalificationTab } from "./PrequalificationTab";
import { PresentBuyerWizard } from "./presentBuyer/PresentBuyerWizard";
import { QualificationInterviewPanel } from "./interview/QualificationInterviewPanel";
import { OutreachTab } from "./outreach/OutreachTab";
import { SubmissionRecordDrawer } from "./recordDrawers";
import { BuyerSectionCard } from "./sections/BuyerSectionCard";
import { BuyerSectionEditModal } from "./sections/BuyerSectionEditModal";
import { buyerSection, type BuyerSectionId } from "./sections/buyerSectionConfig";

type TabKey =
  | "overview"
  | "submissions"
  | "associated-listings"
  | "research"
  | "qualification"
  | "prequalification"
  | "biography"
  | "outreach"
  | "documents"
  | "tasks"
  | "calendar"
  | "messages"
  | "communications"
  | "notes"
  | "activity";

export const BUYER_TAB_ITEMS: ReadonlyArray<EntityTabItem<TabKey>> = [
  { key: "overview", label: "Overview", icon: CROSS_CUTTING_TAB_ICONS.overview },
  { key: "submissions", label: "Submissions", icon: Send },
  // The buyer's shortlist — everything they are looking at, presented or not.
  // Adjacent to Submissions because the two are one workflow read left to right:
  // what is on the list, then what has actually been presented. It is the daily
  // driver, so it stays in the entity-specific block ahead of the cross-cutting
  // six. No badge — badges are deferred for this whole PR.
  { key: "associated-listings", label: "Associated listings", icon: ListChecks },
  // What a search FOUND, beside what is on the list: a candidate attached here
  // becomes a row on Associated listings, so the two read left to right as
  // "what Phoenix turned up, then what the buyer is actually looking at".
  { key: "research", label: "Research", icon: Radar },
  // 🔴 TWO tabs, adjacent — a deliberate reversal of the one-tab merge, from use.
  //
  // The merge argued that the five-dimension assessment and the prequalification
  // funnel answer the same question, so a specialist judging one wants the other
  // in the same view. The first half is true and the conclusion did not follow.
  // Qualification is a JUDGEMENT a specialist makes and records; prequalification
  // is an OUTCOME an applicant produced through a funnel Phoenix does not
  // control, plus its whole invitation lifecycle. One is a form, the other is
  // two lists and four actions, and stacking them made a tab you scroll past
  // half of whichever one you came for.
  //
  // Adjacency was what the argument actually wanted, and adjacency is what it
  // gets. The two are read left to right: what we assessed, then what came back.
  //
  // The `prequalification` KEY is unchanged and now names the tab it always
  // named, so every existing `?tab=prequalification` link lands on the surface
  // its name promised. `qualification` is the new key.
  { key: "qualification", label: "Qualification", icon: ClipboardCheck },
  { key: "prequalification", label: "Prequalification", icon: BadgeCheck },
  // Off Overview and onto its own tab. Thirteen curated fields plus the paste
  // they were extracted from is not a card — it was the heaviest block on the
  // page and it sat between the record and the things a specialist came to do.
  { key: "biography", label: "Biography", icon: User },
  // Texts and emails written from this record, copied into the specialist's own
  // phone and mail client and then logged (Q1). Immediately after Biography:
  // the last entity-specific tab, ahead of the canonical block. Megaphone
  // because nothing else on THIS strip uses it
  // (the buyer TEAM and Reports pages use it for Advertising — a different
  // strip). No badge.
  { key: "outreach", label: "Outreach", icon: Megaphone },
  { key: "documents", label: "Documents", icon: CROSS_CUTTING_TAB_ICONS.documents },
  { key: "tasks", label: "Tasks", icon: CROSS_CUTTING_TAB_ICONS.tasks },
  // 🔴 Calendar sits between Tasks and Messages, and this page did NOT move when
  // Layout Standard v1.5 resolved §3.2 — it was already in the position the
  // resolution chose. 18 entity detail pages used this slot and 2 did not
  // (Listing and BuyerSubmission); those 2 moved here, closing the seam at
  // 20/20. Moving this page the other way would have moved 18 and left 17
  // disagreeing. Pinned by BuyerDetailPages.test.tsx, which did not exist until
  // that change — this strip had nothing holding its order at all.
  { key: "calendar", label: "Calendar", icon: CROSS_CUTTING_TAB_ICONS.calendar },
  { key: "messages", label: "Messages", icon: CROSS_CUTTING_TAB_ICONS.messages },
  { key: "communications", label: "Communications", icon: CROSS_CUTTING_TAB_ICONS.communications },
  { key: "notes", label: "Notes", icon: CROSS_CUTTING_TAB_ICONS.notes },
  { key: "activity", label: "Activity", icon: CROSS_CUTTING_TAB_ICONS.activity },
];

const TAB_ITEMS = BUYER_TAB_ITEMS;
const TAB_KEYS = new Set<string>(TAB_ITEMS.map((t) => t.key));

/**
 * One buyer.
 *
 * The six cross-cutting tabs are the SHARED components every other CRM record
 * uses, reached through the `buyer_lead` registrations. Nothing here is a
 * buyer-specific copy of Notes or Tasks — a second implementation of those is
 * how two records end up disagreeing about what "a note" is.
 */
export function BuyerDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [presenting, setPresenting] = useState(false);
  const [converting, setConverting] = useState(false);
  const [archiveMode, setArchiveMode] = useState<"archive" | "unarchive" | null>(null);
  // 🔴 Lifted out of Overview by Layout Standard §4.1a. The five per-section
  // edits are opened from the HEADER's overflow — exempt chrome, present on
  // every tab — so the modal has to be mounted beside the thing that opens it
  // rather than inside the tab body that used to.
  const [editingSection, setEditingSection] = useState<BuyerSectionId | null>(null);
  // Set only when a shortlist row supplied the listing; cleared on close so the
  // header's own entry point never inherits the last row that was pressed.
  const [presentListingId, setPresentListingId] = useState<string | null>(null);

  // Tab in the URL, so a link to "that buyer's communications" is a link rather
  // than an instruction. An unknown value falls back to Overview.
  const raw = params.get("tab") ?? "";
  const tab: TabKey = TAB_KEYS.has(raw) ? (raw as TabKey) : "overview";
  const setTab = (next: TabKey) => {
    const p = new URLSearchParams(params);
    if (next === "overview") p.delete("tab");
    else p.set("tab", next);
    // 🔴 A drawer belongs to the tab that opened it. Both record parameters are
    // dropped on a tab change, or leaving Submissions with one open would carry
    // it to Associated listings and pop a drawer nobody asked for — over a list
    // that may not even contain the record. It also keeps "at most one record
    // parameter" true for every path through the UI, so the two drawers can
    // never stack.
    p.delete("listing");
    p.delete("submission");
    // The Research tab's selected run rides `?research=` and belongs to that
    // tab for the same reason — and so does its candidate list's filter, sort
    // and page state (R20), which is why every key the list owns is deleted
    // here rather than the handful that happened to exist when this was
    // written. A key the list writes and `RESEARCH_LIST_PARAMS` omits would
    // follow the reader onto the next tab.
    p.delete("research");
    for (const key of RESEARCH_LIST_PARAMS) p.delete(key);
    // The Submissions tab's "Show archived" (D4) belongs to that tab.
    p.delete(SHOW_ARCHIVED_PARAM);
    setParams(p, { replace: true });
  };

  /**
   * Where the Present-a-buyer wizard lands when it finishes here.
   *
   * 🔴 ONE write, not two. `setTab` and `useRecordDrawerParam.open` each build
   * their next `URLSearchParams` from the SAME render's `params`, so calling
   * them in sequence makes the second silently overwrite the first — and no
   * ordering fixes it: `setTab` then `open` loses `tab=submissions`, and the
   * reverse loses the record. The tab change and the record parameter have to
   * be the same `setParams` call.
   *
   * `setTab`'s own rule still holds and is reproduced rather than bypassed: at
   * most one record parameter is live at a time, so `listing` is dropped as the
   * `submission` goes on.
   */
  const openSubmissionOnItsTab = (submissionId: string) => {
    const p = new URLSearchParams(params);
    p.set("tab", "submissions");
    p.delete("listing");
    p.delete("research");
    // Leaving Research here too, so the list's own keys go with its run (R20).
    for (const key of RESEARCH_LIST_PARAMS) p.delete(key);
    p.set("submission", submissionId);
    setParams(p, { replace: true });
  };

  const buyer = useQuery({
    queryKey: buyerKeys.detail(id),
    queryFn: () => buyersApi.get(id),
    enabled: Boolean(id),
  });

  // The URL-derived crumb renders the raw GUID; show the buyer's name instead.
  // `null` while loading is load-bearing — the alternative is a crumb that reads
  // as the record's name and then changes to a different one.
  useBreadcrumbs(
    buyer.data
      ? [
          { label: "Buyers", href: "/buyers" },
          { label: buyer.data.staffDisplayName ?? buyer.data.displayName },
        ]
      : null,
  );

  if (buyer.isLoading) return <Skeleton className="h-64 w-full" />;

  // 🔴 The error branch, which this page was the last in the module to be
  // missing. `!buyer.data` alone rendered "Buyer not found — it may have been
  // removed" for a request that simply did not complete: a claim about the
  // record made on the strength of a broken network call, with no retry, on a
  // QueryClient that will not refetch on focus for five minutes. Only a 404
  // licenses the claim; everything else is a fact about the REQUEST.
  if (buyer.isError && !isNotFoundError(buyer.error)) {
    return (
      <QueryErrorState
        what="this buyer"
        onRetry={() => void buyer.refetch()}
        retrying={buyer.isFetching}
      />
    );
  }

  if (!buyer.data) {
    return <EmptyState title="Buyer not found" description="It may have been removed." />;
  }

  const b = buyer.data;
  // Presenting is a SUBMISSION write on a buyer you own — both halves of the
  // server's own rule, neither re-derived: the permission, and the buyer's
  // server-resolved `canEdit`.
  const canPresent = b.canEdit && hasPermission("Submissions.Manage");
  // Mirrors ConvertToBorrowerButton's own three gates, in the same order: the
  // permission, the row's ownership, and whether it has already happened. The
  // trigger moved into the overflow, so the gate has to travel with it.
  const canConvert =
    hasPermission("Buyers.Convert") && b.canEdit && !b.convertedPartyId;

  // 🔴 Convert is IRREVERSIBLE — the service refuses a second attempt rather
  // than minting a second party for one human — so it belongs behind the ⋯
  // rather than beside the everyday action at equal weight, which is where it
  // used to sit. The whole-record form joins it as the fallback it has always
  // been.
  //
  // 🔴 THE FIVE PER-SECTION EDITS ARE BACK ON THEIR CARDS — Layout Standard
  // §4.1a.1 (v1.5). v1.4 read `edit` out of §4.1a's flat prohibition and lifted
  // them here; v1.5 narrows the rule to the width its own rationale always had.
  // The rationale was that a control sitting beside a one-line précis asks for a
  // decision on information the reader does not have — decisive for a send, an
  // invitation, an escalation. It does not reach a correction to a field
  // rendered six pixels from the control that changes it: there the summary IS
  // the field, and the owning surface would show the same string and nothing
  // more.
  //
  // So the menu keeps what is genuinely RECORD-level and stops duplicating the
  // per-section doors. Two ways into one modal made this menu five-sevenths
  // section edits, which buried the two items that are not — and the one thing
  // the overflow copies bought (reachability from every tab) costs a reader on
  // the Documents tab exactly one click to get back.
  const overflowItems = [
    ...(canConvert
      ? [{ label: "Convert to borrower…", onSelect: () => setConverting(true) }]
      : []),
    ...(b.canEdit
      ? [{ label: "Edit the whole record", onSelect: () => navigate(`/buyers/${b.id}/edit`) }]
      : []),
    // Archive sits LAST, and the label flips with the state rather than the
    // menu carrying both — offering "Unarchive" on a live buyer is an action
    // that would do nothing, which reads as broken rather than as unavailable.
    ...(b.canEdit
      ? [
          {
            label: b.isArchived ? "Unarchive buyer…" : "Archive buyer…",
            onSelect: () => setArchiveMode(b.isArchived ? "unarchive" : "archive"),
          },
        ]
      : []),
  ];

  // Z6d — "via <site> (<owner>)", only when the server says the buyer was
  // created from a partner site's completion.
  const createdVia = createdViaLabel(b, { withOwner: true });

  return (
    <>
      <PageHeader
        eyebrow="Buyer"
        title={b.staffDisplayName ?? b.displayName}
        description={[b.email, b.phone].filter(Boolean).join(" · ") || undefined}
        actions={
          <div className="flex flex-wrap items-center justify-end gap-space-2">
            {/* Converted is a distinct fact from the funnel status — a buyer can
                be Converted and still be worked, so it renders alongside rather
                than replacing the status. */}
            {/* 🔴 This linked to `/parties/{id}`, which the SPA does not
                register — a live 404 on a merged page. A borrower party's
                record is `/borrowers/:id`, which is what the three independent
                resolvers (the messaging picker, RelatedEntityResolver and the
                activity provider) have always used.

                Gated on the permission that gates the DESTINATION, the way
                canConvert already is. Correcting the route alone would have
                traded a 404 for a permission wall for any buyer consultant
                without Borrowers.Read, which is not a fix. */}
            {b.convertedPartyId && hasPermission("Borrowers.Read") && (
              <Link
                to={`/borrowers/${b.convertedPartyId}`}
                className="text-small text-gold-shadow hover:underline"
              >
                View borrower party
              </Link>
            )}
            {/* 🔴 ONE gold primary. The header used to carry six controls of
                equal weight — a badge, a status trigger, Send email, Present,
                Convert and Edit — so nothing on it read as the thing to do, and
                the two irreversible ones (Convert, Send) sat at the same weight
                as the everyday one. Presenting is what a specialist opens a
                buyer to do; everything else is behind the ⋯. */}
            {canPresent && (
              <Button
                size="sm"
                onClick={() => setPresenting(true)}
                data-testid="buyer-present"
              >
                <Send size={14} strokeWidth={1.5} aria-hidden /> Present to a listing
              </Button>
            )}
            {/* D4 — the buyer's referring broker, only when one is set. */}
            <ReferredByBadge buyerId={b.id} />
            {/* Still its own control rather than an overflow item: it owns both
                its trigger and its modal, and driving it from outside would take
                a controlled-open prop on a file this workstream does not own.
                `size` is not forwarded either — see the PR description. */}
            <BuyerStatusControl buyerLeadId={b.id} status={b.status} canEdit={b.canEdit} />
            {overflowItems.length > 0 && (
              <OverflowMenu
                items={overflowItems}
                label="More buyer actions"
                data-testid="buyer-overflow"
              />
            )}
          </div>
        }
      />

      {/* Created and modified, in the header block where every other record
          carries them. It answers "is this stale?" — the question a specialist
          asks before trusting a figure — and until now the page did not say. */}
      <div className="-mt-space-2 mb-space-4 text-caption text-gray-mid">
        Created <Timestamp value={b.createdAt} />
        {createdVia && <span data-testid="buyer-created-via"> {createdVia}</span>}
        {b.modifiedAt && (
          <>
            {" · Last updated "}
            <Timestamp value={b.modifiedAt} />
          </>
        )}
      </div>

      {/* 🔴 THE ARCHIVED BANNER, above the ladder and on every tab.
          An archived buyer's record opens and stays fully editable — that is
          the whole difference between this and the soft-delete lifecycle — so
          the banner is the ONLY thing telling a reader why they cannot find
          this buyer in the list, the queue or any picker. It carries the
          reason, the actor and the date because "archived" alone leaves the
          next person to guess, and it carries its own door back out. */}
      {b.isArchived && (
        <div
          className="mb-space-4 rounded-md border border-status-warning bg-white-warm p-space-3"
          data-testid="buyer-archived-banner"
        >
          <div className="flex flex-wrap items-start justify-between gap-space-3">
            <div className="grid gap-space-1 min-w-0">
              <p className="text-small font-medium text-onyx m-0">
                This buyer is archived. They are hidden from the buyers list, the
                assignment queue and the buyer pickers — nothing has been deleted, and
                this record is still editable.
              </p>
              {b.archiveReason && (
                <p className="text-small text-onyx m-0">“{b.archiveReason}”</p>
              )}
              <p className="text-caption text-gray-mid m-0">
                Archived
                {b.archivedByName ? ` by ${b.archivedByName}` : ""}
                {b.archivedAtUtc && (
                  <>
                    {" "}
                    <Timestamp value={b.archivedAtUtc} />
                  </>
                )}
              </p>
            </div>
            {b.canEdit && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setArchiveMode("unarchive")}
                data-testid="buyer-unarchive"
              >
                Unarchive
              </Button>
            )}
          </div>
        </div>
      )}

      {/* 🔴 Both, always: the condensed chip ladder AND the raw status badge.
          A chip alone is a lossy read — "Won" does not say whether the buyer
          converted or purchased — and the badge alone is what the page had, which
          says where they are without saying how far that is. `PhaseLadder`
          renders the pair from one resolved model so neither can be mounted
          without the other. The status MODAL still offers all eleven statuses;
          nothing about the state machine or the milestone stamps changed. */}
      <div className="mb-space-4">
        <PhaseLadder
          ladder={describeBuyerPhase(b.status)}
          label="Buyer progress"
          data-testid="buyer-phase-ladder"
        />
      </div>

      {/* D15 — the "Next step" strip: owner, status, ageing, the next
          follow-up and the two verbs that move them. Header chrome on every
          tab (§4.1a exempts the header), so it sits ABOVE the tab bar and never
          inside a tab body.

          🔴 ON EVERY TAB INCLUDING RESEARCH, AND THAT WAS MEASURED BEFORE IT
          WAS LEFT ALONE. Hiding it on the Research tab was considered — that
          tab is the tallest surface on the page — and the numbers say no.
          At 1440x900 the strip is 127px plus a 24px margin, and the tab bar
          starts at y=522, so dropping it would buy 151px of FIRST SCREEN.

          It buys nothing after that. `EntityTabBar`'s nav is `sticky top-0`
          and this strip is not, so the 151px scrolls away on its own and the
          only permanently occupied chrome is the 34px tab bar. What a per-tab
          hide would cost is paid every time: chrome that is present on every tab
          but one is not chrome, it is an exception a reader cannot
          predict, and the page's whole top jumps on the way in and on the way
          out. Owner, status and "is this urgent" are also exactly what a
          specialist reading a candidate list wants beside them.

          If the strip is too tall it is too tall on every tab, and the
          move is a persisted collapse ON THE STRIP — the `useUserPreference`
          mechanism the sidebar in `AppShell` now uses — which keeps it chrome,
          keeps the DOM order this page's tests pin, and lets a reader who
          disagrees put it away once. That is a change to
          `components/buyers/BuyerNextStepStrip.tsx`. */}
      <BuyerNextStepStrip buyer={b} />

      {/*
        🔴 NO `showLabels`, AND THAT IS A MEASUREMENT RATHER THAN A TASTE.
        Fourteen labelled tabs needed 2172px of content (measured before the
        Outreach tab made it fifteen, so the spill below has only grown); the
        widest desktop box this strip gets is 1536px (a 1900px window, less the 256px sidebar and
        the content padding). Measured with `showLabels` on, at four widths:

            1024px → 704px box, 1468px spill, 10 of 14 tabs off-screen
            1280px → 960px box, 1212px spill,  9 of 14 tabs off-screen
            1440px → 1120px box, 1052px spill, 8 of 14 tabs off-screen
            1900px → 1536px box,  636px spill, 5 of 14 tabs off-screen

        The strip cannot WRAP — `EntityTabBar`'s scroller is `flex` with
        `shrink-0` tabs and `whitespace-nowrap` labels, and Layout Standard
        §6.8 requires exactly that — so the cost of labels is not a second row,
        it is tabs pushed off the right-hand edge of a scroller that gives no
        visual hint it scrolls. Five hidden tabs on the widest monitor anyone
        here uses is the same failure the Loan detail page reverted labels for
        in #611, and the four numbers above say it is worse at every width a
        specialist actually works at.

        `e2e/buyer-tab-strip-labels.spec.ts` holds the strip to fitting, so a
        future flip of this prop fails there with the spill printed rather than
        shipping a strip whose last five tabs nobody finds. What would make
        labels affordable is `shortLabel` on the long ones — a per-item prop
        `EntityTabBar` already supports — and that is a copy decision, not a
        prop flip.
      */}
      <EntityTabBar
        ariaLabel="Buyer sections"
        testIdPrefix="buyer"
        items={TAB_ITEMS}
        value={tab}
        onChange={setTab}
        className="mb-space-4"
      />

      {/* One wizard for all three of this page's entry points — the header, the
          Submissions empty state, and a shortlist row. The buyer half is locked
          because it is the record the specialist is standing on; a shortlist row
          supplies the listing too, so that pairing needs no search at all. */}
      {canPresent && (
        <PresentBuyerWizard
          open={presenting}
          onClose={() => {
            setPresenting(false);
            setPresentListingId(null);
          }}
          buyerLeadId={b.id}
          buyerName={b.displayName}
          listingId={presentListingId ?? undefined}
          onCreated={openSubmissionOnItsTab}
        />
      )}

      {/* The controlled half of the convert flow, mounted here because its
          trigger is now an overflow item rather than the component's own
          button. */}
      {canConvert && (
        <ConvertToBorrowerModal
          buyer={b}
          open={converting}
          onClose={() => setConverting(false)}
        />
      )}

      {/* PAGE level, like every other overlay on this page. A Modal mounted
          inside collapsible or tab-scoped content paints nothing while still
          setting body overflow:hidden — an invisible scroll-locked page, which
          this codebase has shipped once already. */}
      {archiveMode && (
        <ArchiveBuyerModal
          buyer={b}
          mode={archiveMode}
          onClose={() => setArchiveMode(null)}
        />
      )}

      {/* Mounted at PAGE level, beside the header overflow that opens it —
          §4.1a put the trigger there, and a modal mounted inside the Overview
          body could not be reached from any other tab. */}
      {editingSection && (
        <BuyerSectionEditModal
          section={buyerSection(editingSection)}
          buyer={b}
          open
          onClose={() => setEditingSection(null)}
        />
      )}

      {tab === "overview" && (
        <BuyerOverview
          buyer={b}
          onEditSection={setEditingSection}
          onNavigateTab={setTab}
        />
      )}
      {tab === "submissions" && (
        <BuyerSubmissionsTab
          buyerLeadId={id}
          buyerName={b.displayName}
          buyerEmail={b.email ?? null}
          onPresent={canPresent ? () => setPresenting(true) : undefined}
          canCompose={canPresent}
        />
      )}
      {/* `canManage` is the server-resolved `canEdit` every other write
          affordance on this page gates on — the ownership rule is never
          re-derived client-side. */}
      {tab === "associated-listings" && (
        <AssociatedListingsTab
          buyerLeadId={id}
          canManage={b.canEdit}
          onPresentListing={
            canPresent
              ? (row) => {
                  setPresentListingId(row.listingId);
                  setPresenting(true);
                }
              : undefined
          }
        />
      )}
      {tab === "research" && (
        <ResearchTab buyerLeadId={id} canEdit={b.canEdit} buyerDisplayName={b.displayName} />
      )}
      {tab === "qualification" && (
        <QualificationTabBody
          buyerLeadId={b.id}
          buyerName={b.displayName}
          qualification={b.qualification}
          status={b.status}
          canEdit={b.canEdit}
          onOpenCommunications={() => setTab("communications")}
        />
      )}
      {tab === "prequalification" && (
        <PrequalificationTab buyerLeadId={b.id} canEdit={b.canEdit} />
      )}
      {tab === "biography" && (
        <BuyerBiographyPanel
          buyerLeadId={b.id}
          biography={b.biography}
          canEdit={b.canEdit}
        />
      )}
      {tab === "outreach" && <OutreachTab buyerId={b.id} />}
      {tab === "documents" && (
        <DocumentPanel ownerType="BuyerLead" ownerId={id} rootName={b.displayName} />
      )}
      {tab === "tasks" && (
        <TaskListMini
          relatedEntityType="BuyerLead"
          relatedEntityId={id}
          addButtonLabel="+ Add task linked to this buyer"
        />
      )}
      {/* No permission gate on the panel's Schedule button, deliberately: all
          five Appointments.* permissions are absent from RolePermissionMap, so
          gating it the way Tasks gates its own would hide scheduling from every
          non-admin. AppointmentListMini gates on entityArchived alone. */}
      {tab === "calendar" && (
        <AppointmentListMini
          relatedEntityType="BuyerLead"
          relatedEntityId={id}
          entityLabel={b.displayName}
        />
      )}
      {tab === "messages" && (
        <RelatedConversationsPanel
          resourceType="buyer_lead"
          resourceId={id}
          entityLabel={b.displayName}
        />
      )}
      {tab === "communications" && (
        <CommunicationsTabPanel ownerType="buyer_lead" ownerId={id} ownerLabel={b.displayName} />
      )}
      {tab === "notes" && <BuyerNotesTab buyer={b} />}
      {tab === "activity" && <ActivityTabPanel ownerType="buyer_lead" ownerId={id} />}
    </>
  );
}

/**
 * Overview — identity facts left, orientation right.
 *
 * <p>🔴 <b>It used to be one column of eleven stacked cards.</b> A specialist
 * opening a record cold read about twenty label/value rows before meeting a
 * status signal, and the three heavyweight interactive panels — the
 * prequalification link, the qualification drawer's trigger and the
 * thirteen-field biography form — sat BETWEEN the identity facts and the
 * milestone ladder, so the record's own story was interrupted twice by things to
 * fill in.</p>
 *
 * <p>Now: four identity cards in a two-column grid, each individually editable,
 * and a right rail carrying the things you GLANCE at rather than read —
 * milestones, recent activity, attribution. The two heavyweight panels have
 * left Overview entirely for tabs of their own (Layout Standard §4.2 — adding
 * entity-specific tabs needs no amendment).</p>
 *
 * <p>The rail is Layout Standard §4.1's sanctioned Recent Activity preview,
 * capped at five, plus two blocks that earn their place beside it. §7.5.4 says
 * the Deal's rail does not generalise and a rail elsewhere needs its own
 * justification; this is ours.</p>
 */
function BuyerOverview({
  buyer: b,
  onEditSection,
  onNavigateTab,
}: {
  buyer: BuyerLeadDetailDto;
  /**
   * Where to send a reader who presses a milestone rung.
   *
   * <p>Layout Standard §4.1a — an Overview may summarise, reference and LINK; it
   * may not mutate or send. A rung stays read-only and hands the reader to the
   * tab that owns the control, which is exactly the shape the submission
   * Overview's milestone list already uses.</p>
   */
  onNavigateTab: (tab: TabKey) => void;
  /**
   * 🔴 The DOOR lives in the header, and this is the handle on it.
   *
   * <p>Layout Standard §4.1a — an Overview may summarise, reference and link; it
   * may not mutate. The four identity cards each carried their own pencil, and a
   * pencil is an edit affordance however small it is. §4.1a exempts the header
   * in full, so the five per-section edits moved into its `OverflowMenu` and the
   * cards below are read-only.</p>
   *
   * <p><b>UPDATE — the door came back to the card, and the header no longer
   * duplicates it.</b> Layout Standard §4.1a.1 (v1.5) narrows the Overview rule
   * to WORKFLOW actions and states the resolution in terms: "correcting a field
   * on the record you are looking at is data maintenance, not workflow, and
   * belongs where the data is displayed." The licence is ADJACENCY, not the tab
   * you are standing on — which is why each pencil opens a modal carrying
   * exactly the fields its own card renders, and why the record-note card (which
   * never left) was already the precedent §4.1a.1 cites.</p>
   *
   * <p>Per-section targeting was preserved throughout, in both directions: the
   * overflow opened the SAME modal these pencils open, so a specialist who has
   * just been given a phone number is still not handed forty fields.</p>
   */
  onEditSection: (section: BuyerSectionId) => void;
}) {
  // 🔴 The card's own pencil is gated on the SERVER-resolved `canEdit`, not on a
  // permission re-derived here — the same answer every other write affordance on
  // this page reads, so an enabled control and a refused save cannot disagree.
  // 🔴 The one read Overview makes, and it serves TWO facts.
  //
  // NDA state is a fact about a SUBMISSION, and so is a follow-up date. Both
  // come off this one aggregate scan (see `BUYER_SUBMISSION_ROLLUP_SCAN`). It is
  // deliberately NOT the Submissions tab's query: that tab pages server-side
  // through `useRecordSubmissions`, and sharing a key with it once capped both at
  // one page. So the two are separate requests, each refreshed by the same
  // prefix invalidation, and they can be briefly out of step after a change.
  const submissionParams = submissionsForBuyer(b.id);
  const submissions = useQuery({
    queryKey: buyerSubmissionKeys.list(submissionParams),
    queryFn: () => buyerSubmissionsApi.list(submissionParams),
  });


  // The strip stays presentational: it is handed resolved states, not a query.
  // The three non-ready states are distinct on purpose — "still loading" and
  // "the read failed" must never render as a claim about the record.
  const failed = submissions.isError || !submissions.data;
  const rows = submissions.data?.data ?? [];

  const nda: BuyerNdaChip = submissions.isLoading
    ? { status: "loading" }
    : failed
      ? { status: "unavailable" }
      : {
          status: "ready",
          onFile: rows.filter((s) => s.ndaOnFile).length,
          // 🔴 The server's total, never `rows.length`. A row count is a fact
          // about the page that arrived; this chip is a claim about the buyer.
          // They were the same number only while the read was capped and the
          // table silently dropped everything past it.
          submissions: submissions.data?.totalCount ?? rows.length,
        };

  const followUp: BuyerFollowUpChip = submissions.isLoading
    ? { status: "loading" }
    : failed
      ? { status: "unavailable" }
      : {
          status: "ready",
          // The buyer's OWN follow-up date no longer exists (follow-ups are
          // WorkTasks, shown on the next-step strip above); only the
          // submissions' dates roll up here.
          ...resolveNextFollowUp(
            null,
            rows.map((s) => s.nextFollowUpDate),
          ),
        };

  return (
    <>
      <BuyerSummaryStrip buyer={b} nda={nda} followUp={followUp} />

      {/*
        🔴 CONTAINER-DRIVEN, NOT VIEWPORT-DRIVEN — Table & Tile Standard §12.
        This was `lg:grid-cols-3` + `lg:col-span-2` over `md:grid-cols-2`, and
        the card width it produced was NOT MONOTONIC in window width: measured
        at 1280 -> 1024 -> 900 -> 768 -> 640px, a card went 357 -> 299 -> 402 ->
        336 -> 592px. Twice, narrowing the window made the cards WIDER, because
        two independent viewport breakpoints fired at different moments and
        neither was measuring this box.
      */}
      <ResponsiveSplit
        gap="lg"
        mainMinWidth="34rem"
        railWidth={RECORD_RAIL_WIDTH}
        // The rail holds a milestone ladder; it does not want a share of the
        // leftover width on a wide monitor, the main column does. See the prop.
        railGrows={false}
        rail={
          <div className="grid gap-space-5 content-start">
            <OverviewRail b={b} onEditSection={onEditSection} onNavigateTab={onNavigateTab} />
          </div>
        }
        main={
        <ResponsiveGrid cols={2} minColWidth={RECORD_CARD_MIN_WIDTH} gap="lg">
        {/*
          The record itself — four cards, two across, each with its own pencil.
          🔴 Per-section rather than one Edit button for the page: a specialist
          who has just been given a phone number should not be handed forty
          fields. The modal sends only its own section's keys, so every field
          outside it is untouched on the wire.

          🔴 The pencils are back — Layout Standard §4.1a.1 (v1.5). They are the
          house rule now, not an exception to one: an edit is licensed where the
          value it changes is rendered beside it. See `overflowItems` above for
          what the header kept.
        */}
          <BuyerSectionCard
            section={buyerSection("contact")}
            buyer={b}
            canEdit={b.canEdit}
            onEdit={() => onEditSection("contact")}
            leading={
              // The one inline write on this card, and NOT a patch: the quick
              // pick posts to the assignment endpoints (a reason, the grant
              // ledger) and invalidates through `useBuyerAssignments`, so this
              // row re-renders from the server's answer. It decides for itself
              // whether it is a control or text — `canReassign` is
              // server-resolved, never derived here — which is why the section
              // config still leaves the owner out: the patch does not accept
              // it, so a field there would be a silent no-op.
              <>
                <DetailField
                  label="Consultant"
                  value={
                    <SpecialistQuickPick
                      buyerLeadId={b.id}
                      currentSpecialistUserId={b.assignedSpecialistUserId}
                      currentSpecialistName={b.assignedSpecialistName}
                      canReassign={b.canReassign ?? false}
                      canUnassign={b.canUnassign ?? false}
                      buyerDisplayName={b.displayName}
                      size="sm"
                    />
                  }
                />
                {/* The queue this lead belongs to — NOT the same fact as who is
                    working it (D2): a specialist may hold a lead belonging to a
                    team they have since left, so the two are said separately.
                    Absent renders the em dash: an unrouted lead is a state. */}
                <DetailField
                  label="Team"
                  value={b.buyerTeamName ?? undefined}
                  data-testid="buyer-team"
                />
              </>
            }
          />
          <BuyerSectionCard
            section={buyerSection("capacity")}
            buyer={b}
            canEdit={b.canEdit}
            onEdit={() => onEditSection("capacity")}
          />
          <BuyerSectionCard
            section={buyerSection("criteria")}
            buyer={b}
            canEdit={b.canEdit}
            onEdit={() => onEditSection("criteria")}
          />
          <BuyerSectionCard
            section={buyerSection("background")}
            buyer={b}
            canEdit={b.canEdit}
            onEdit={() => onEditSection("background")}
          />
        </ResponsiveGrid>
        }
      />
    </>
  );
}

/**
 * The right rail. Extracted so the Overview body reads as one `ResponsiveSplit`
 * call rather than a hundred lines of JSX threaded through a prop.
 */
function OverviewRail({
  b,
  onEditSection,
  onNavigateTab,
}: {
  b: BuyerLeadDetailDto;
  onEditSection: (id: BuyerSectionId) => void;
  onNavigateTab: (tab: TabKey) => void;
}) {
  return (
    <>
          {/*
            Stamps, never derived from the current status (L6): a buyer who
            regresses must not lose the record that a live conversation happened,
            because the funnel report counts the stamp.

            In the rail rather than the body because it is a glance — seven rungs,
            each either reached or not. The dated history of how it got that way
            is the Activity tab, which is directly beneath it.
          */}
          <DetailSection title="Milestones">
            <MilestoneList<TabKey>
              onNavigate={onNavigateTab}
              items={[
                { label: "First contact attempt", at: b.firstContactAttemptAtUtc },
                { label: "First live conversation", at: b.firstLiveConversationAtUtc },
                // Placed after the first live conversation and before the
                // prequalification: educating a buyer is early qualification
                // work, done before they are presented to anyone, not a step
                // that waits on the funnel. It carries `to` so the rung is a
                // door to the tab that owns the send — the Overview rule says
                // this list may summarise and link, never send.
                {
                  label: "Buyer educated",
                  at: b.educationSentAtUtc,
                  to: "qualification",
                },
                { label: "Prequalification completed", at: b.prequalCompletedAtUtc },
                { label: "PFS received", at: b.pfsReceivedAtUtc },
                { label: "Proof of funds verified", at: b.proofOfFundsVerifiedAtUtc },
                { label: "Qualified", at: b.qualifiedAtUtc, to: "qualification" },
                { label: "Converted to borrower", at: b.convertedAtUtc },
              ]}
            />
          </DetailSection>

          {/* 🔴 BACK ON OVERVIEW, which is where this panel's own documentation
              always said it lived and where §4.1a wants it: a read-only summary
              that references and links, with no door of its own.

              The one-tab merge had swept it INSIDE the tab it links to, so
              "Manage prequalification →" pointed at the surface it was already
              rendered on, one screen above the actions it was offering to take
              you to. It also duplicated the facts the tab renders itself. Here
              it is what it was written to be — the answer at a glance, beside
              the milestone row that reports the same event, with the way
              through to where the acting happens. */}
          <PrequalificationLinkPanel buyerLeadId={b.id} canEdit={b.canEdit} />

          {/* Layout Standard §4.1's sanctioned rail card — read-only, last five
              events, with its own way through to the full timeline. */}
          <OverviewActivityPreview
            ownerType="buyer_lead"
            ownerId={b.id}
            detailPath={`/buyers/${b.id}`}
          />

          <BuyerAttributionCard buyer={b} onEdit={() => onEditSection("attribution")} />
    </>
  );
}

/**
 * "{name} · {channel} · {team}" for an attributed lead, undefined (→ the em
 * dash) for one with no advertisement. The channel token is the wire value, so
 * it goes through the one label map the advertising surfaces share.
 */
function describeAdvertisement(b: BuyerLeadDetailDto): string | undefined {
  if (!b.advertisementId) return undefined;
  return [
    b.advertisementName?.trim() || "Advertisement",
    b.advertisementChannel ? channelLabel(b.advertisementChannel) : null,
    b.advertisementTeamName?.trim() || null,
  ]
    .filter(Boolean)
    .join(" · ");
}

/**
 * Where this buyer came from — collapsed by default.
 *
 * <p>Eleven fields nobody reads on an ordinary visit, and they are the fields a
 * marketing question is answered from rather than a specialist's. Collapsed
 * keeps them one press away without spending eleven rows of the rail on them.</p>
 *
 * <p><b>Lead source stays inline.</b> It is a save-on-change select that has
 * worked since PR 3b, and moving it into the section modal would make a working
 * control depend on a server change that has not shipped yet.</p>
 */
function BuyerAttributionCard({
  buyer: b,
  onEdit,
}: {
  buyer: BuyerLeadDetailDto;
  onEdit: () => void;
}) {
  const [open, setOpen] = useState(false);
  const section = buyerSection("attribution");

  return (
    <DetailSection
      title="Attribution"
      action={
        /* Two controls, and they are different KINDS of thing. Show / Hide is
           reading — "expanding a row in place" is named as permitted in §4.1a
           itself. The pencil is the fifth per-section door, back on its card
           under §4.1a.1 with the other four; without it the eleven attribution
           fields would be reachable from nowhere once the overflow stopped
           carrying them. */
        <div className="flex items-center gap-space-2">
          <Button
            variant="text"
            size="sm"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            data-testid="buyer-attribution-toggle"
          >
            {open ? "Hide" : "Show"}
          </Button>
          {b.canEdit && (
            <Button
              variant="text"
              size="sm"
              onClick={onEdit}
              aria-label="Edit Attribution"
              data-testid="buyer-section-edit-attribution"
            >
              <Pencil size={14} strokeWidth={1.5} aria-hidden /> Edit
            </Button>
          )}
        </div>
      }
    >
      <DetailFieldList>
        {/* Always visible, above the fold of this card: the advertisement is
            the attribution now (§2.2), so it is the one row here a specialist
            reads on an ordinary visit. Resolved server-side — name, channel,
            team — so this never renders a GUID and never has to read the
            advertising surface, which an F2 cannot. */}
        <DetailField
          label="Advertisement"
          value={describeAdvertisement(b)}
          data-testid="buyer-advertisement"
        />
        <DetailField
          label="Source"
          value={<LeadSourceField buyerLeadId={b.id} value={b.source} canEdit={b.canEdit} />}
        />
        <BuyerReferredBy buyerId={b.id} canEdit={b.canEdit} />
        {open &&
          section.fields.map((f) => (
            <DetailField
              key={f.key}
              label={f.label}
              value={b[f.key] as string | null | undefined}
              wide={isWideFieldType(f)}
            />
          ))}
      </DetailFieldList>
    </DetailSection>
  );
}

/**
 * The Notes tab — the shared composer and feed, then the record's own two text
 * blocks.
 *
 * <p>🔴 <b>Both blocks used to be on Overview,</b> where the "Notes on file" card
 * sat beside a TAB called Notes holding entirely different content. Two surfaces
 * with one name is how a note gets typed into the wrong one and nobody sees it.
 * They belong together, on the tab whose name they share.</p>
 *
 * <p>The shared panel goes FIRST because Layout Standard §4.7 puts the composer
 * at the top of the Notes tab. That is why these two land below the feed rather
 * than between the composer and the thread: the panel exposes no slot between
 * the two, and inventing one belongs in the shared component rather than here.
 * Called out in the PR description.</p>
 */
function BuyerNotesTab({ buyer: b }: { buyer: BuyerLeadDetailDto }) {
  const [editing, setEditing] = useState(false);

  return (
    <div className="grid gap-space-5">
      <NotesTabPanel ownerType="buyer_lead" ownerId={b.id} />

      <DetailSection
        title="Notes on file"
        action={
          b.canEdit ? (
            <Button
              variant="text"
              size="sm"
              onClick={() => setEditing(true)}
              aria-label="Edit notes on file"
              data-testid="buyer-section-edit-record-note"
            >
              Edit
            </Button>
          ) : null
        }
      >
        <div className="text-caption text-gray-mid mb-space-2">
          The note field on this buyer&apos;s record, captured at intake. Team
          discussion belongs in the composer above.
        </div>
        <div className="whitespace-pre-wrap text-small text-onyx">
          {b.notes || <span className="text-gray-mid">Nothing recorded.</span>}
        </div>
      </DetailSection>

      {b.rawPastedText && <OriginalPaste buyer={b} />}

      {editing && (
        <BuyerSectionEditModal
          section={buyerSection("record-note")}
          buyer={b}
          open
          onClose={() => setEditing(false)}
        />
      )}
    </div>
  );
}

/**
 * The paste this record was extracted from.
 *
 * Kept and shown rather than discarded after extraction: a mis-read figure is
 * only recoverable if a human can read what the model actually saw.
 *
 * <p>🔴 <b>The model identifier is gone from the copy.</b> It was rendered to a
 * specialist as "Extracted by claude-sonnet-5" — a string that tells
 * the reader nothing they can act on, dates the product the moment the model
 * changes, and names a vendor in a CRM field for no operational reason. The
 * value is still on the DTO and still stored, so a systematic extraction error
 * remains traceable in the data; it simply is not the caption.</p>
 */
function OriginalPaste({ buyer }: { buyer: BuyerLeadDetailDto }) {
  const [open, setOpen] = useState(false);

  return (
    <DetailSection title="Original paste">
      <div className="text-caption text-gray-mid mb-space-2">
        Extracted automatically from pasted text. Kept so a mis-read figure can
        always be checked against what was actually pasted.
      </div>
      <button
        type="button"
        className="text-small text-gold-shadow hover:underline"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? "Hide the original text" : "Show the original text"}
      </button>
      {open && (
        <pre className="mt-space-2 font-body text-small text-onyx bg-white-warm border border-border-light rounded-md p-space-3 whitespace-pre-wrap max-h-96 overflow-y-auto">
          {buyer.rawPastedText}
        </pre>
      )}
    </DetailSection>
  );
}

/**
 * The Qualification tab — TWO steps, at equal weight.
 *
 * <p>🔴 Qualification is knowing the buyer AND having educated them. It used to
 * be one card, so a buyer who had been assessed read as finished while never
 * having been told how a Phoenix transaction works — and a buyer who cannot
 * answer a broker's questions about the financing is a weaker candidate however
 * well they assess. Both cards therefore render always, and an untouched buyer
 * shows two outstanding steps rather than one.</p>
 *
 * <p>The assessment card is now the qualification INTERVIEW: the specialist
 * records what the buyer said and the server computes the verdicts. The
 * five-dropdown editor it replaced is gone, along with the write path behind
 * it — a historical assessment still renders, read-only.</p>
 */
function QualificationTabBody({
  buyerLeadId,
  buyerName,
  qualification,
  status,
  canEdit,
  onOpenCommunications,
}: {
  buyerLeadId: string;
  buyerName?: string;
  qualification?: BuyerLeadQualificationDto | null;
  status: BuyerLeadStatus;
  canEdit: boolean;
  onOpenCommunications: () => void;
}) {
  return (
    <div className="grid gap-space-4">
      <QualificationInterviewPanel
        buyerLeadId={buyerLeadId}
        buyerName={buyerName}
        qualification={qualification}
        status={status}
        canEdit={canEdit}
      />
      <BuyerEducationCard
        buyerLeadId={buyerLeadId}
        canEdit={canEdit}
        onOpenCommunications={onOpenCommunications}
      />
    </div>
  );
}

/**
 * This buyer's submissions, inline. The full cross-cutting view lives on the
 * Submissions page — this is the one-buyer slice of it.
 *
 * <p><b>Rows OPEN, they do not navigate.</b> A row writes `?submission=` and the
 * submission's own body renders in a drawer over this buyer, so reading one
 * costs neither the tab nor the list. The drawer's "Open full page" is the way
 * to the record's own route; since Layout Standard v1.8 (§7.4a item 3a) the
 * drawer's header also carries the page's own status control, under a title
 * that names the listing, so a status can be moved without leaving this
 * buyer.</p>
 */
function BuyerSubmissionsTab({
  buyerLeadId,
  buyerName,
  buyerEmail,
  onPresent,
  canCompose,
}: {
  buyerLeadId: string;
  /** The combined intro's recipient, named before a draft exists. */
  buyerName: string;
  buyerEmail: string | null;
  /** Absent when this reader may not present — the copy names the action, so
   *  the button that satisfies it has to be here or nowhere. */
  onPresent?: () => void;
  /** May this reader tick listings for a combined intro email — the row's own
   *  `canEdit` AND `Submissions.Manage`, the same gate as presenting. The
   *  server applies it again on every draft, send and copy. */
  canCompose: boolean;
}) {
  // D4 "Show archived" — in the link, like the firm-wide list's filters, so a
  // copied link opens the same view. One write per change.
  const [params, setParams] = useSearchParams();
  const includeArchived = params.get(SHOW_ARCHIVED_PARAM) === "1";
  const setIncludeArchived = (next: boolean) => {
    const p = new URLSearchParams(params);
    if (next) p.set(SHOW_ARCHIVED_PARAM, "1");
    else p.delete(SHOW_ARCHIVED_PARAM);
    setParams(p, { replace: true });
  };

  const list = useRecordSubmissions({ buyerLeadId }, { includeArchived });
  const rows = list.rows;

  // The tick selection — survives a page or sort change on purpose; see
  // `combinedIntroSelection.ts` for why this one tab is the exception. It feeds
  // the combined intro AND the bar's Archive / Delete (D3a).
  const selection = useCombinedIntroSelection(buyerLeadId, rows);
  const { hasPermission } = useAuth();
  const removal = useSubmissionRemoval({
    onRemoved: selection.remove,
    canDeleteAtAll: hasPermission("Submissions.Delete"),
  });
  const pickedOn = `${list.sort}:${list.page}`;
  // The buyer's unsent combined draft, if any — offered as "Resume draft", and
  // asked about before a new draft replaces it.
  const openDraft = useOpenCombinedIntroDraft(buyerLeadId, canCompose);
  const [composer, setComposer] = useState<CombinedIntroComposerStart | null>(null);

  const drawer = useRecordDrawerParam("submission");
  const openRow = rows.find((s) => s.id === drawer.openId);
  const columns = withArchivedRows(
    [
      ...(canCompose ? [combinedIntroSelectColumn(selection, pickedOn)] : []),
      ...BUYER_SUBMISSION_COLUMNS(drawer.open),
      {
        key: "actions",
        header: <ActionsColumnHeader />,
        align: "right" as const,
        render: (s: BuyerSubmissionSummaryDto) => (
          <SubmissionRowMenu target={removalTargetOf(s)} removal={removal} />
        ),
      },
    ],
    { pillColumnKey: "status", keepKeys: ["actions", "combined-intro-select"] },
  );
  const toggle = (
    <div className="flex justify-end">
      <ShowArchivedToggle checked={includeArchived} onChange={setIncludeArchived} />
    </div>
  );
  // Mounted from every branch below, so a refresh on a URL naming a submission
  // reopens it while the list behind it is still arriving.
  const drawerNode = drawer.openId ? (
    <SubmissionRecordDrawer
      key={drawer.openId}
      submissionId={drawer.openId}
      // SHORT — the drawer heading wraps rather than truncating.
      title={resolvedShortListingName(openRow?.listingDisplayName, openRow?.listingBusinessName) ?? undefined}
      subtitle="Submission"
      onClose={drawer.close}
    />
  ) : null;

  if (list.isLoading)
    return (
      <>
        <Skeleton className="h-40 w-full" />
        {drawerNode}
      </>
    );

  // 🔴 The error branch goes BEFORE the empty branch, as it does everywhere else
  // in this module. A failed read fell straight through to "No submissions yet
  // — present this buyer to a listing to start a conversation", which tells a
  // specialist that this buyer has never been presented to anything when the
  // truth is that the request did not complete. That is a claim about the
  // record made on the strength of a broken network call, and acting on it
  // means presenting a buyer who may already be in play.
  //
  // No 404 split: a list scoped to a buyer answers empty for a buyer with no
  // submissions, so a 404 here is not a fact about this buyer either.
  if (list.isError) {
    return (
      <>
        <QueryErrorState
          what="this buyer's submissions"
          onRetry={list.refetch}
          retrying={list.isFetching}
        />
        {drawerNode}
      </>
    );
  }

  // A search that matched nothing is not "no submissions": the table (and its
  // search box) stays, saying nothing matches.
  if (rows.length === 0 && !list.activeSearch && !list.search) {
    return (
      <>
        {toggle}
        {list.hasHiddenArchived ? (
          <EmptyState
            title="Every submission is archived"
            description="Turn on Show archived to see them."
          />
        ) : (
          <EmptyState
            title="No submissions yet"
            description="Present this buyer to a listing to start a conversation."
            action={onPresent ? { label: "Present to a listing", onClick: onPresent } : undefined}
          />
        )}
        {drawerNode}
        {removal.node}
      </>
    );
  }

  return (
    <>
      {/* `DetailSection` publishes no surface of its own, so without this the
          table resolves to "page" and paints the screen's own full-onyx header
          inside a record. Declared here rather than pinned with `SubTable`'s
          `surface` escape hatch, which would survive this tab being moved into
          a drawer and be wrong there. */}
      <Surface kind="card">
        <div className="flex min-w-0 flex-col gap-space-3">
          {canCompose && openDraft.data ? (
            <div className="flex justify-end">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => openDraft.data && setComposer({ kind: "resume", email: openDraft.data })}
                data-testid="combined-intro-resume"
              >
                {combinedIntroCopy.resumeDraft(openDraft.data.listings.length)}
              </Button>
            </div>
          ) : null}
          {toggle}
          {canCompose && (
            <CombinedIntroSelectionBar
              selection={selection}
              onSend={() => setComposer({ kind: "new", picks: selection.picks })}
              actions={
                <>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={removal.pending}
                    onClick={() => removal.archiveMany(selection.picks.map(removalTargetOfPick))}
                    data-testid="submissions-bar-archive"
                  >
                    {submissionArchiveCopy.archive}
                  </Button>
                  {removal.canDeleteAtAll && (
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={removal.pending}
                      onClick={() => removal.requestDelete(selection.picks.map(removalTargetOfPick))}
                      data-testid="submissions-bar-delete"
                    >
                      {submissionArchiveCopy.delete}
                    </Button>
                  )}
                </>
              }
            />
          )}
          <RecordSubmissionsTable
            state={list}
            caption="Submissions"
            sortLabel="Sort this buyer's submissions by"
            searchPlaceholder="Search listing or agent (name, email, phone)…"
            testId="buyer-submissions-table"
            columns={columns}
            rowSelected={canCompose ? (s) => selection.isSelected(s.id) : undefined}
          />
        </div>
      </Surface>
      {composer ? (
        <CombinedIntroComposer
          buyerLeadId={buyerLeadId}
          buyerName={buyerName}
          buyerEmail={buyerEmail}
          start={composer}
          openDraft={openDraft.data ?? null}
          onClose={() => setComposer(null)}
          onDone={selection.clear}
        />
      ) : null}
      {drawerNode}
      {removal.node}
    </>
  );
}

/** The buyer tab's Show archived key (D4), dropped on a tab change. */
const SHOW_ARCHIVED_PARAM = "archived";

function removalTargetOf(s: BuyerSubmissionSummaryDto): SubmissionRemovalTarget {
  return {
    id: s.id,
    listingName: listingNameOrFallback(s.listingDisplayName, s.listingBusinessName),
    isArchived: s.isArchived,
    canArchive: s.canArchive,
    archiveBlockedReason: s.archiveBlockedReason,
    canDelete: s.canDelete,
    deleteBlockedReason: s.deleteBlockedReason,
  };
}

function removalTargetOfPick(p: CombinedIntroPick): SubmissionRemovalTarget {
  return {
    id: p.submissionId,
    listingName: p.listingName,
    isArchived: p.isArchived,
    canArchive: p.canArchive,
    archiveBlockedReason: p.archiveBlockedReason,
    canDelete: p.canDelete,
    deleteBlockedReason: p.deleteBlockedReason,
  };
}

/**
 * A buyer's submissions, as columns.
 *
 * 🔴 THE ROWS STAY RICH. Headed by the LISTING, because that is what
 * distinguishes one row from another on a buyer, and carrying the asking price
 * beside the offer so the gap between them is readable without opening
 * anything. Adopting shared chrome is about how a table looks and when it grows
 * controls, not about how much a row is allowed to say.
 */
const BUYER_SUBMISSION_COLUMNS = (
  open: (id: string) => void,
): SubTableColumn<BuyerSubmissionSummaryDto>[] => [
  {
    key: "listing",
    header: "Listing",
    // Sorting is SERVER-side through the sort control; a per-column accessor
    // would order the 25 rows in hand and claim to have ordered the list.
    render: (s) => (
      /*
        SHORT in the cell, WHOLE on hover — and now on keyboard focus too. The
        old reveal was a native `title`, which satisfies the hover half of the
        truncation contract and silently fails the other: `title` never opens on
        focus, so the pitch was unreachable to anyone driving this page from the
        keyboard, and it is reachable nowhere else on the tab.

        `Tooltip` directly rather than `TruncateCell`, because the two strings
        differ on purpose — `TruncateCell` reveals ITS OWN text, which would
        show the short name twice.
      */
      <div className="min-w-0">
        <Tooltip label={listingNameOrFallback(s.listingDisplayName, s.listingBusinessName)}>
          <button
            type="button"
            className="min-w-0 max-w-full truncate text-left text-gold-shadow hover:underline"
            onClick={() => open(s.id)}
            data-testid="buyer-submission-open"
          >
            {listingShortNameOrFallback(s.listingDisplayName, s.listingBusinessName)}
          </button>
        </Tooltip>
        {/* Ruling D6: an already-introduced listing stays selectable and says
            so. A column of its own would be empty on most rows (Table & Tile
            §6.3), so the fact rides under the name. */}
        {s.introEmailSentAtUtc && <IntroSentCaption at={s.introEmailSentAtUtc} />}
      </div>
    ),
  },
  // Addendum B (B1): no COLUMNS picker here, so the agent's phone and email
  // ride a second line under the name.
  ...listingAgentColumns<BuyerSubmissionSummaryDto>((s) => s.listingAgent, { withContactLine: true }),
  {
    key: "status",
    header: "Status",
    render: (s) => (
      <StatusBadge tone={submissionStatusTone(s.status)}>{buyerLabel(s.status)}</StatusBadge>
    ),
  },
  {
    key: "asking",
    header: "Asking",
    align: "right",
    render: (s) => <TableValue value={money(s.listingAskingPrice)} />,
  },
  {
    key: "offer",
    header: "Deal structure price",
    align: "right",
    render: (s) => <TableValue value={money(s.dealStructurePrice)} />,
  },
  {
    key: "followUp",
    header: "Follow-up",
    render: (s) =>
      s.nextFollowUpDate ? <Timestamp value={s.nextFollowUpDate} /> : <MissingValue />,
  },
];

/**
 * The Overview strip's ROLL-UP read of a buyer's submissions.
 *
 * <p>🔴 This used to be shared with the Submissions tab, on the reasoning that
 * one query key meant the strip could never disagree with the table below it.
 * The agreement was real and the price was too high: BOTH were capped at fifty
 * rows with no pager and no caption, so a buyer with fifty-one submissions had
 * one silently missing from the table AND a strip that said "50" — two surfaces
 * agreeing on the wrong number.</p>
 *
 * <p>They ask different questions and now read accordingly. The strip wants an
 * aggregate over ALL of them; the tab wants a page. The count it reports comes
 * from the server's <c>totalCount</c>, so it is EXACT no matter what this read
 * fetches — that is the invariant, and it is pinned by a test.</p>
 *
 * <p>The page size is the server's own clamp ceiling
 * (<c>BuyerSubmissionService.PageSize</c> allows at most 200), which is what
 * the NDA tally and the soonest-follow-up scan run over. Residual, stated
 * rather than hidden: a buyer pursuing more than two hundred businesses at once
 * would have those two chips computed over the first two hundred. The COUNT
 * stays exact regardless, and no such buyer exists; fixing the tally properly
 * needs a server-side aggregate rather than a bigger page.</p>
 */
const BUYER_SUBMISSION_ROLLUP_SCAN = 200;

function submissionsForBuyer(buyerLeadId: string) {
  return { buyerLeadId, page: 1, pageSize: BUYER_SUBMISSION_ROLLUP_SCAN };
}

function money(value?: number | null) {
  return value == null ? null : formatCurrency(value);
}
